<app-breadcrumb [home]="home" [items]="breadcrumbs" />

<div class="queue-header">
  <header class="page-header">
    <h2>Comitê de crédito</h2>
  </header>
  <div class="queue-header__actions">
    <app-button
      [label]="'Atualizar'"
      [icon]="'refresh'"
      [theme]="'outline-grey'"
      [disabled]="loading()"
      [type]="'button'"
      (sendButtonClick)="load()" />
    @if (canConfigure()) {
      <app-button
        [label]="'Configurar comitê'"
        [icon]="'cog'"
        [theme]="'outline-grey'"
        [routerLink]="'/dashboard/management/back-office/support/parametrizacao'"
        [queryParams]="{ open: 'credit-committee' }"
        [type]="'button'" />
    }
  </div>
</div>

@if (loading()) {
  <div class="queue-state">
    <app-cog-loading-spinner [size]="64" />
    <p>Carregando a fila do comitê...</p>
  </div>
} @else if (error()) {
  <div class="queue-error" role="alert">
    <i class="pi pi-exclamation-triangle"></i>
    <span class="queue-error__text">{{ error() }}</span>
    <app-button
      [label]="'Tentar de novo'"
      [theme]="'outline-grey'"
      [type]="'button'"
      (sendButtonClick)="load()" />
  </div>
} @else {
  @let k = kpis();
  @let mine = split().mine;
  @let others = split().others;

  <!-- 1. Precisa de você -->
  <section class="queue-section" aria-labelledby="queue-mine-title">
    @if (mine.length) {
      <p class="queue-section__eyebrow">Precisa de você</p>
      <h3 id="queue-mine-title" class="queue-section__title">
        {{ mine.length }} {{ mine.length === 1 ? 'deal espera' : 'deals esperam' }} o seu voto
        @if (k.awaitingMyVoteOverdue > 0) {
          <span
            class="queue-tag queue-tag--danger"
            pTooltip="Sócios e comercial já foram avisados do atraso."
            tooltipPosition="top">
            {{ k.awaitingMyVoteOverdue }} com prazo vencido
          </span>
        }
      </h3>

      <div class="vote-cards">
        @for (item of mine; track item.dealId + '-' + item.round) {
          <article class="vote-card" [class.vote-card--overdue]="item.isOverdue">
            <div class="vote-card__top">
              <div class="vote-card__who">
                <a
                  class="vote-card__company"
                  [routerLink]="dealRoute(item)"
                  [queryParams]="{ stage: 'committee' }"
                  [title]="item.companyName">
                  {{ item.companyName }}
                </a>
                <span class="vote-card__meta">
                  #{{ item.dealNumber }}
                  @if (item.round > 1) {
                    · rodada {{ item.round }}
                  }
                  @if (item.submittedByName) {
                    · enviado por {{ item.submittedByName }}
                  }
                </span>
              </div>
              <span
                class="queue-tag"
                [class.queue-tag--danger]="item.isOverdue"
                [class.queue-tag--neutral]="!item.isOverdue"
                [pTooltip]="'Prazo: ' + dateTime(item.deadlineAt)"
                tooltipPosition="top">
                <i
                  class="pi"
                  [class.pi-exclamation-circle]="item.isOverdue"
                  [class.pi-clock]="!item.isOverdue"></i>
                {{ deadline(item) }}
              </span>
            </div>

            <p class="vote-card__amount">
              {{ item.amount | currency: item.currencyCode : 'symbol' : '1.0-0' : 'pt-BR' }}
            </p>

            <div class="vote-card__bottom">
              <div class="vote-card__score">
                <app-committee-voter-badges [voters]="item.voters" />
                <span class="vote-card__score-text">{{ score(item) }}</span>
                @if (item.openContestCount > 0) {
                  <span class="queue-tag queue-tag--warning">
                    {{ item.openContestCount }}
                    {{
                      item.openContestCount === 1
                        ? 'contestação em aberto'
                        : 'contestações em aberto'
                    }}
                  </span>
                }
              </div>
              <app-button
                [label]="'Votar'"
                [theme]="'darkblue'"
                [size]="'small'"
                [routerLink]="dealRoute(item)"
                [queryParams]="{ stage: 'committee' }"
                [type]="'button'" />
            </div>
          </article>
        }
      </div>
    } @else {
      <div class="queue-clear" id="queue-mine-title" role="status">
        <i class="pi pi-check-circle"></i>
        <span>
          Nada esperando o seu voto agora. Quem vota no comitê recebe o pedido no sino e por e-mail
          quando um deal é enviado.
        </span>
      </div>
    }
  </section>

  <!-- 2. Em votação com os outros -->
  @if (others.length) {
    <section class="queue-section" aria-labelledby="queue-others-title">
      <p class="queue-section__eyebrow">
        {{ mine.length ? 'Em votação com os outros' : 'Em votação' }}
      </p>
      <h3 id="queue-others-title" class="queue-section__title queue-section__title--small">
        {{ others.length }} {{ others.length === 1 ? 'deal' : 'deals' }}
        @if (othersContests() > 0) {
          <span class="queue-tag queue-tag--warning">
            {{ othersContests() }}
            {{ othersContests() === 1 ? 'contestação em aberto' : 'contestações em aberto' }}
          </span>
        }
        @if (othersOverdue() > 0) {
          <span
            class="queue-tag queue-tag--danger"
            pTooltip="Sócios e comercial já foram avisados do atraso."
            tooltipPosition="top">
            {{ othersOverdue() }} com prazo vencido
          </span>
        }
      </h3>
      <ng-container
        *ngTemplateOutlet="queueTable; context: { $implicit: others, decided: false }" />
    </section>
  } @else if (!mine.length) {
    <p class="queue-empty">
      Nenhum deal em votação agora. Os deals entram aqui quando o comercial os envia ao comitê, na
      etapa Análise.
    </p>
  }

  <!-- 3. Decididos nos últimos 30 dias -->
  <section class="queue-section">
    @if (k.decided > 0) {
      <button
        type="button"
        class="decided-toggle"
        [attr.aria-expanded]="showDecided()"
        aria-controls="queue-decided"
        (click)="toggleDecided()">
        <span class="decided-toggle__icon"><i class="pi pi-inbox"></i></span>
        <span class="decided-toggle__text">
          <strong>Decididos nos últimos 30 dias: {{ k.decided }}</strong>
          <span>
            {{ k.approved }} {{ k.approved === 1 ? 'aprovado' : 'aprovados' }} · {{ k.denied }}
            {{ k.denied === 1 ? 'negado' : 'negados' }}
          </span>
        </span>
        <span class="decided-toggle__bar" aria-hidden="true">
          <span class="decided-toggle__bar-ok" [style.width.%]="approvedPercent()"></span>
        </span>
        <i
          class="pi decided-toggle__chevron"
          [class.pi-chevron-down]="!showDecided()"
          [class.pi-chevron-up]="showDecided()"></i>
      </button>
      @if (showDecided()) {
        <div id="queue-decided">
          <ng-container
            *ngTemplateOutlet="queueTable; context: { $implicit: decidedItems(), decided: true }" />
        </div>
      }
    } @else {
      <p class="decided-none">Nenhum comitê decidido nos últimos 30 dias.</p>
    }
  </section>
}

<ng-template #queueTable let-rows let-decided="decided">
  <div class="queue-table-wrap">
    <table class="queue-table">
      <colgroup>
        <col class="queue-table__col-deal" />
        <col class="queue-table__col-amount" />
        <col class="queue-table__col-votes" />
        <col class="queue-table__col-date" />
        <col class="queue-table__col-situation" />
        <col class="queue-table__col-action" />
      </colgroup>
      <thead>
        <tr>
          <th>Deal</th>
          <th class="queue-table__num">Valor</th>
          <th>Votos</th>
          <th>{{ decided ? 'Decidido em' : 'Prazo do voto' }}</th>
          <th>Situação</th>
          <th><span class="sr-only">Ação</span></th>
        </tr>
      </thead>
      <tbody>
        @for (item of rows; track item.dealId + '-' + item.round) {
          @let s = situation(item);
          <tr>
            <td>
              <a
                class="queue-table__deal"
                [routerLink]="dealRoute(item)"
                [queryParams]="{ stage: 'committee' }"
                [title]="item.companyName">
                {{ item.companyName }}
              </a>
              <span class="queue-table__meta">
                #{{ item.dealNumber }}
                @if (item.round > 1) {
                  · rodada {{ item.round }}
                }
              </span>
            </td>
            <td class="queue-table__num">
              {{ item.amount | currency: item.currencyCode : 'symbol' : '1.0-0' : 'pt-BR' }}
            </td>
            <td>
              <app-committee-voter-badges [voters]="item.voters" />
            </td>
            <td class="queue-table__date">
              @if (decided) {
                {{ dateTime(item.decidedAt) }}
              } @else {
                <span [class.queue-table__date--danger]="item.isOverdue">{{ deadline(item) }}</span>
                <span class="queue-table__meta">{{ dateTime(item.deadlineAt) }}</span>
              }
            </td>
            <td>
              <span class="queue-tag queue-tag--{{ s.tone }}">{{ s.label }}</span>
            </td>
            <td class="queue-table__action">
              <app-button
                [label]="'Abrir'"
                [theme]="'outline-grey'"
                [size]="'small'"
                [routerLink]="dealRoute(item)"
                [queryParams]="{ stage: 'committee' }"
                [type]="'button'" />
            </td>
          </tr>
        }
      </tbody>
    </table>
  </div>
</ng-template>
